/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  combineReducers,
  composeReducers,
  composeSetters,
  fieldValidationSetter,
  formStateSnapshot,
  getInvalidFields,
  hasFormStateError,
  markFormStateAsTouched,
  newDirtyRow,
  onChangeReducer,
  removeDirtyRow,
  resetCreatedDate,
  resetDirtyType,
  setCreatedDate,
  setDirty,
  setEntityField,
  setEntityFields,
  setNestedEntityField,
  setRecalculationNeeded,
  setUpdatedDirtyType,
  Table,
  toOnChangeReducer,
  whenNotModified,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { arrayIterator } from '@gh/core-util';
import { constant, first, isNil } from 'lodash';
import {
  getPbCcStructureId,
  getPbHousePlanId,
  PbCcStructure,
  PbCostCenter,
  PbDetails,
  PbDetailsSaveResponse,
  PbHousePlan,
  PbHousePlanFilter,
  validatePbStructureField,
} from '../../resources/price-book.model';
import {
  hasPbMasterInfoErrors,
  pbCcTable,
  PbDetailsState,
  PbDetailsStateEvent,
  PbDetailsStateEventType,
  PbDetailsTab,
  pbHousePlanTable,
  pbStructureTable,
  queryExistingCostCenters,
  queryExistingHousePlans,
  queryExistingStructures,
  queryInvalidStructures,
  queryPbStructure,
} from './pb-details.state';

const setStructureField = composeSetters(
  setEntityField,
  setUpdatedDirtyType,
  fieldValidationSetter(validatePbStructureField));

const rootFlagsReducer = composeReducers(dirtyFlagReducer, recalculationNeededFlagReducer);

export const pbDetailsReducer = composeReducers(
  onChangeReducer(
    mainReducer,
    toOnChangeReducer(rootFlagsReducer),
  ),
  selectionReducer,
);

const housePlanTabReducer = composeReducers(
  combineReducers({
    housePlanFilter: housePlanFilterReducer,
    dirtyHousePlans: dirtyHousePlansReducer,
  }),
);

const structuresTabReducer = onChangeReducer(
  combineReducers({
    costCenters: costCentersReducer,
    structures: structuresReducer,
  }),
  structuresOnChangeReducer);

function mainReducer(state: PbDetailsState, event: PbDetailsStateEvent): PbDetailsState {
  switch (event.type) {
    case PbDetailsStateEventType.Load:
      return loadDetails(state, event.details, event.mode, event.sourcePriceBookId);
    case PbDetailsStateEventType.MergeOnCalculate:
      return mergeDetailsOnCalculate(state, event.calculation);
    case PbDetailsStateEventType.MergeOnSave:
      return mergeDetailsOnSave(state, event.calculation);
    case PbDetailsStateEventType.SetMasterInfo:
      return setEntityField(state, 'masterInfo', event.masterInfo);
    case PbDetailsStateEventType.SetDefaults:
      return setEntityField(state, 'defaults', event.defaults);
    case PbDetailsStateEventType.SetRootField:
      return setEntityField(state, event.field, event.value);
    case PbDetailsStateEventType.UpdateHpFilter:
    case PbDetailsStateEventType.HousePlanAddSet:
    case PbDetailsStateEventType.HousePlanUpdate:
    case PbDetailsStateEventType.HousePlanRemove:
      return housePlanTabReducer(state, event);
    case PbDetailsStateEventType.CostCenterAddSet:
    case PbDetailsStateEventType.CostCenterRemove:
    case PbDetailsStateEventType.CostCenterLoad:
    case PbDetailsStateEventType.StructureAdd:
    case PbDetailsStateEventType.StructureSetField:
    case PbDetailsStateEventType.StructureRemove:
      return structuresTabReducer(state, event);
    default:
      return state;
  }
}

function dirtyFlagReducer(state: PbDetailsState, event: PbDetailsStateEvent): PbDetailsState {
  switch (event.type) {
    case PbDetailsStateEventType.SetMasterInfo:
    case PbDetailsStateEventType.SetDefaults:
    case PbDetailsStateEventType.SetRootField:
    case PbDetailsStateEventType.HousePlanAddSet:
    case PbDetailsStateEventType.HousePlanUpdate:
    case PbDetailsStateEventType.HousePlanRemove:
    case PbDetailsStateEventType.CostCenterAddSet:
    case PbDetailsStateEventType.CostCenterRemove:
    case PbDetailsStateEventType.StructureAdd:
    case PbDetailsStateEventType.StructureSetField:
    case PbDetailsStateEventType.StructureRemove:
      return setDirty(state);
    default:
      return state;
  }
}

function recalculationNeededFlagReducer(state: PbDetailsState, event: PbDetailsStateEvent): PbDetailsState {
  switch (event.type) {
    case PbDetailsStateEventType.StructureAdd:
    case PbDetailsStateEventType.StructureSetField:
      return setRecalculationNeeded(state);
    default:
      return state;
  }
}

function housePlanFilterReducer(filter: PbHousePlanFilter, event: PbDetailsStateEvent): PbHousePlanFilter {
  switch (event.type) {
    case PbDetailsStateEventType.UpdateHpFilter:
      return setEntityField(filter, event.field, event.value);
    default:
      return filter;
  }
}

function selectionReducer(state: PbDetailsState, event: PbDetailsStateEvent): PbDetailsState {
  switch (event.type) {
    case PbDetailsStateEventType.SelectTab:
      return setNestedEntityField(state, ['selection', 'tab'], event.tab);
    case PbDetailsStateEventType.HousePlanAddSet:
    case PbDetailsStateEventType.HousePlanUpdate:
    case PbDetailsStateEventType.HousePlanSelect:
    case PbDetailsStateEventType.HousePlanRemove:
    case PbDetailsStateEventType.MergeOnSave:
      return setNestedEntityField(
        state,
        ['selection', 'housePlan'],
        selectedHousePlanReducer(state.selection.housePlan, event));
    case PbDetailsStateEventType.CostCenterRemove:
      return setNestedEntityField(
        state,
        ['selection', 'costCenterId'],
        event.costCenterId === state.selection.costCenterId ? void 0 : state.selection.costCenterId);
    case PbDetailsStateEventType.StructureRemove:
      return setNestedEntityField(
        state,
        ['selection', 'structureId'],
        event.structureId === state.selection.structureId ? void 0 : state.selection.structureId);
    case PbDetailsStateEventType.CostCenterSelect:
      return setNestedEntityField(state, ['selection', 'costCenterId'], event.costCenterId);
    case PbDetailsStateEventType.NavigateToInvalid:
      return navigateToInvalidReducer(state, event);
    case PbDetailsStateEventType.ResetFocusedCell:
      return setNestedEntityField(state, ['selection', 'structureFocusedCell'], void 0);
    default:
      return state;
  }
}

function navigateToInvalidReducer(state: PbDetailsState, _: PbDetailsStateEvent): PbDetailsState {
  const masterInfoForm = state.masterInfo;
  const defaultsForm = state.defaults;

  if (hasPbMasterInfoErrors(masterInfoForm)) {
    return setEntityField(state, 'masterInfo', markFormStateAsTouched(masterInfoForm));
  }

  if (hasFormStateError(defaultsForm)) {
    return setEntityFields(state, {
      selection: setEntityField(state.selection, 'tab', PbDetailsTab.PRICE_BOOK),
      defaults: markFormStateAsTouched(defaultsForm),
    });
  }

  const invalidStructure = first(queryInvalidStructures(state));

  if (invalidStructure) {
    return setEntityField(state, 'selection', setEntityFields(state.selection, {
      tab: PbDetailsTab.ITEMS_AND_COMPONENTS,
      costCenterId: invalidStructure.costCenterId,
      structureId: getPbCcStructureId(invalidStructure),
      structureFocusedCell: {
        id: getPbCcStructureId(invalidStructure),
        field: first(getInvalidFields(invalidStructure))!, // tslint:disable-line:no-non-null-assertion
      },
    }));
  }

  return state;
}

function selectedHousePlanReducer(housePlan: Maybe<PbHousePlan>, event: PbDetailsStateEvent): Maybe<PbHousePlan> {
  switch (event.type) {
    // case PbDetailsStateEventType.HousePlanAdd:
    //   return housePlan;
    case PbDetailsStateEventType.HousePlanUpdate:
      if (housePlan && getPbHousePlanId(housePlan) === getPbHousePlanId(event.housePlan)) {
        return event.housePlan;
      } else {
        return housePlan;
      }
    case PbDetailsStateEventType.HousePlanSelect:
      return event.housePlan;
    case PbDetailsStateEventType.HousePlanRemove:
      return housePlan && getPbHousePlanId(housePlan) === getPbHousePlanId(event.housePlan) ? void 0 : housePlan;
    case PbDetailsStateEventType.MergeOnSave: {
      const selectedHpWasUpdated = housePlan
        && event.calculation.housePlans.some((hp) => getPbHousePlanId(housePlan) === getPbHousePlanId(hp));

      return housePlan && selectedHpWasUpdated ? resetCreatedDate(resetDirtyType(housePlan)) : housePlan;
    }
    default:
      return housePlan;
  }
}

function dirtyHousePlansReducer(housePlans: Table<PbHousePlan>, event: PbDetailsStateEvent): Table<PbHousePlan> {
  switch (event.type) {
    case PbDetailsStateEventType.HousePlanAddSet:
      return pbHousePlanTable.mergeAll(housePlans, event.housePlans.map(setCreatedDate), newDirtyRow);
    case PbDetailsStateEventType.HousePlanUpdate:
      return pbHousePlanTable.mergeById(
        housePlans,
        getPbHousePlanId(event.housePlan),
        constant(setUpdatedDirtyType(event.housePlan)));
    case PbDetailsStateEventType.HousePlanRemove:
      return pbHousePlanTable.mergeById(
        housePlans,
        getPbHousePlanId(event.housePlan),
        (row) => !row || whenNotModified(row) ? removeDirtyRow(event.housePlan) : removeDirtyRow(row));
    default:
      return housePlans;
  }
}

function costCentersReducer(costCenters: Table<PbCostCenter>,
                            event: PbDetailsStateEvent): Table<PbCostCenter> {
  switch (event.type) {
    case PbDetailsStateEventType.CostCenterAddSet:
      return pbCcTable.mergeAll(costCenters, event.costCenters, newDirtyRow);
    case PbDetailsStateEventType.CostCenterRemove:
      return pbCcTable.mergeById(costCenters, event.costCenterId, removeDirtyRow);
    case PbDetailsStateEventType.CostCenterLoad:
      return pbCcTable.mergeById(costCenters, event.costCenterId, (cc) => setEntityField(cc, 'preloaded', true));
    default:
      return costCenters;
  }
}

function structuresReducer(structures: Table<PbCcStructure>,
                           event: PbDetailsStateEvent): Table<PbCcStructure> {
  switch (event.type) {
    case PbDetailsStateEventType.CostCenterLoad:
      return pbStructureTable.insertAll(structures, event.structures);
    case PbDetailsStateEventType.StructureAdd:
      return pbStructureTable.mergeAll(structures, event.structures, newDirtyRow);
    case PbDetailsStateEventType.StructureRemove:
      return pbStructureTable.mergeById(structures, event.structureId, removeDirtyRow);
    case PbDetailsStateEventType.StructureSetField:
      return pbStructureTable.mergeById(
        structures,
        event.structureId,
        (row: PbCcStructure) => setStructureField(row, event.name, event.value));
    default:
      return structures;
  }
}

function structuresOnChangeReducer(oldState: PbDetailsState,
                                   newState: PbDetailsState,
                                   event: PbDetailsStateEvent): PbDetailsState {
  switch (event.type) {
    case PbDetailsStateEventType.StructureAdd:
      return setEntityField(newState, 'costCenters', pbCcTable.mergeAllById(
        newState.costCenters,
        event.structures.map((structure) => structure.costCenterId),
        setUpdatedDirtyType));
    case PbDetailsStateEventType.StructureRemove:
    case PbDetailsStateEventType.StructureSetField:
      return setEntityField(newState, 'costCenters', pbCcTable.mergeById(
        newState.costCenters,
        queryPbStructure(oldState, event.structureId)!.costCenterId, // tslint:disable-line:no-non-null-assertion
        setUpdatedDirtyType));
    default:
      return newState;
  }
}

function loadDetails(state: PbDetailsState,
                     details: PbDetails,
                     formMode: FormMode,
                     sourcePriceBookId?: ID): PbDetailsState {
  const {
    priceBookId, name, memo, version, usage, releaseDate, repriceDate,
    withdrawalDate, housePlanIds, housePlans, costCenters, items,
  } = details;

  return {
    ...state,
    priceBookId,
    sourcePriceBookId,
    memo,
    version: version || 0,
    formMode,
    masterInfo: formStateSnapshot({ name, usage, releaseDate, repriceDate, withdrawalDate }),
    defaults: formStateSnapshot(details.defaults),
    dirtyHousePlans: pbHousePlanTable.create(arrayIterator(housePlans ?
      housePlans
      : housePlanIds.map((housePlanId) => <PbHousePlan>({ housePlanId })))),
    costCenters: pbCcTable.create(arrayIterator(costCenters)),
    structures: pbStructureTable.create(arrayIterator(items || [])),
    selection: { tab: PbDetailsTab.PRICE_BOOK },
    isRecalculationNeeded: false,
    isDirty: isNil(priceBookId),
  };
}

function mergeDetailsOnSave(state: PbDetailsState, calculation: PbDetailsSaveResponse): PbDetailsState {
  const costCenters = pbCcTable.create(arrayIterator(queryExistingCostCenters(state).map(resetDirtyType)));
  const structures = pbStructureTable.create(arrayIterator(queryExistingStructures(state).map(resetDirtyType)));

  return {
    ...state,
    priceBookId: calculation.priceBookId,
    version: calculation.version,
    isDirty: false,
    isRecalculationNeeded: false,
    dirtyHousePlans: pbHousePlanTable.create(arrayIterator(queryExistingHousePlans(state).map(resetDirtyType))),
    // tslint:disable-next-line:no-non-null-assertion
    costCenters,
    structures: pbStructureTable.mergeAll(structures, calculation.items, (oldStructure, newStructure) =>
      oldStructure ? resetDirtyType(setEntityField(oldStructure, 'price', newStructure.price)) : void 0),
  };
}

function mergeDetailsOnCalculate(state: PbDetailsState, calculation: PbDetailsSaveResponse): PbDetailsState {
  return setEntityFields(state, {
    structures: pbStructureTable.mergeAll(state.structures, calculation.items, (oldStructure, newStructure) =>
      setEntityField(oldStructure!, 'price', newStructure.price)), // tslint:disable-line:no-non-null-assertion
    isRecalculationNeeded: false,
  });
}
